import { users } from '@/api'
import { apiDelete, apiPost, apiPut } from '@/api/http'
import type { User } from '@/types/user'

export const userService = {
  /**
   * Récupère la liste de tous les utilisateurs depuis l'API.
   * @returns Une promesse qui résout en un tableau d'utilisateurs.
   */
  getAll: users.getAll,

  /**
   * Alias pour getAll pour la compatibilité avec UserManagementTable
   */
  getAllUsers: users.getAll,

  /**
   * Met à jour un utilisateur existant.
   * @param id L'ID de l'utilisateur à mettre à jour.
   * @param data Les données de l'utilisateur à mettre à jour.
   * @returns Une promesse qui résout en l'utilisateur mis à jour.
   */
  update: users.update,

  resetPassword: users.resetPassword,

  /**
   * Crée un nouvel utilisateur
   */
  async create(userData: {
    firstName: string
    lastName: string
    email: string
    role: string
    password: string
    isActive: boolean
  }): Promise<User> {
    const res = await apiPost<User>('/users', userData)
    return res
  },

  /**
   * Met à jour le mot de passe d'un utilisateur
   */
  async updatePassword(id: number, currentPassword: string, newPassword: string): Promise<void> {
    await apiPut<void>(`/users/${id}/password`, { currentPassword, newPassword })
  },

  /**
   * Active/désactive un utilisateur (soft delete)
   */
  async toggleStatus(id: number, isActive: boolean) {
    const res = await apiPut<any>(`/users/${id}`, { isActive })
    if (res && res.data) return res.data
    return res
  },

  /**
   * Suppression physique (hard delete)
   */
  async hardDelete(id: number) {
    const res = await apiDelete<any>(`/users/${id}`)
    if (res && res.data) return res.data
    return res
  },

  /**
   * Validation manuelle d'email par admin
   */
  async validateEmail(id: number) {
    const res = await apiPut<any>(`/users/${id}/verify-email`, {})
    if (res && res.data) return res.data
    return res
  },

  async deleteSelf(): Promise<void> {
    await apiDelete<void>('/users/me')
  },

  async restoreSelf(): Promise<User> {
    const res = await apiPut<User>('/users/me/restore', {})
    return res
  },

  async hardDeleteSelf(): Promise<void> {
    await apiDelete<void>('/users/me/permanent')
  },

  /**
   * Met à jour le rôle d'un utilisateur (pour chercheurs)
   */
  async updateUserRole(userId: number, newRole: string): Promise<void> {
    await apiPut<void>(`/users/${userId}/role`, { role: newRole })
  },

  // ... autres méthodes (resetPassword, etc.) ...
}
